import { IField } from '../FieldRenderer/types';
import { dotNotation } from '../FieldRenderer/dotNotation';
import { parseField } from '../FieldRenderer/parseField';
import { isNonNull } from '../../utils';
import { ReactNode } from 'react';
import {
    IVendorCustomAttributesData,
    IVendorCustomAttributesXml,
} from '../../services/Company/Services/types/CompanyVendorAttributes';

function asArray<T>(value: T | T[] | null | undefined): T[] {
    if (value == null) {
        return [];
    }

    return Array.isArray(value) ? value : [value];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

function getRootInner(value: unknown): IVendorCustomAttributesData {
    if (!isPlainObject(value) || !isPlainObject(value.root)) {
        return {};
    }

    return { ...value.root };
}

function hasPreservedSiblings(inner: IVendorCustomAttributesData): boolean {
    return Object.keys(inner).some((key) => {
        if (key === 'attributes' || key.startsWith('@')) {
            return false;
        }

        const sibling = inner[key];
        return sibling != null && sibling !== '';
    });
}

/**
 * Parses attributes that were previously serialized to an array of IField[]
 * @param value attributes that were previously serialized
 * @returns the fields
 */
export function parseAttributes(value: unknown): IField[] {
    if (!value) {
        return [];
    }

    try {
        const fields = asArray(getRootInner(value).attributes);

        return fields.map(parseField).filter(isNonNull);
    } catch (e) {
        console.error('Error parsing attribute array, returning empty array', e);
        return [];
    }
}

/**
 * Serializes attributes so they can be stored in an "XML" field.
 * Copies existing root siblings (including opaque `dataMapping`) so they are not wiped.
 */
export function serializeAttributes(attributes: IField[], existingXml?: unknown): IVendorCustomAttributesXml | null {
    const rest = { ...getRootInner(existingXml) };
    delete rest.attributes;

    if (attributes.length === 0) {
        if (!hasPreservedSiblings(rest)) {
            return null;
        }

        return { root: rest };
    }

    return { root: { ...rest, attributes } };
}

export function validateAttributesDotNotation(context: string[], attributes: IField[], errors: ReactNode[]) {
    const labelsCounter: Record<string, number> = {};
    const dataBindingKeyCounter: Record<string, number> = {};

    attributes.forEach((field) => {
        if (field.dataBindingKey) {
            dataBindingKeyCounter[field.dataBindingKey] ??= 0;
            dataBindingKeyCounter[field.dataBindingKey]++;
        }

        if (field.label) {
            labelsCounter[field.label] ??= 0;
            labelsCounter[field.label]++;
        }
    });

    const duplicateLabels = Object.entries(labelsCounter)
        .filter(([, count]) => count > 1)
        .map(([label]) => label);
    const duplicateDataBindingKeys = Object.entries(dataBindingKeyCounter)
        .filter(([, count]) => count > 1)
        .map(([dataBindingKey]) => dataBindingKey);

    if (duplicateLabels.length > 0) {
        errors.push(`Duplicate field names: ${duplicateLabels.join(', ')}`);
    }
    if (duplicateDataBindingKeys.length > 0) {
        errors.push(`Duplicate data binding keys: ${duplicateDataBindingKeys.join(', ')}`);
    }

    attributes.forEach((field, i) => {
        if (!field.label) {
            errors.push(`${[...context, `Field ${i + 1}`].join(' / ')} is missing label`);
        } else {
            dotNotation.validateDotNotation(
                [...context, field.label, 'Field Data Binding Key'].join(' / '),
                field.dataBindingKey,
                errors
            );
        }
    });
}
